import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { PublicNav } from "@/components/PublicChrome";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { CARE_HOME_TYPES } from "@/components/common";
import { IntroOfferBanner } from "@/components/IntroOfferBanner";
import { PasswordInput } from "@/components/PasswordInput";
import { ShieldCheck, Clock, Users, FileCheck2, MessageCircle, ExternalLink } from "lucide-react";

const submitLabel = (loading, isLogin) => { if (loading) return "Please wait…"; return isLogin ? "Log in" : "Start free trial"; };

export default function Auth({ mode }) {
  const nav = useNavigate();
  const { login, signup } = useAuth();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", care_home_name: "", job_role: "", beds: "" });
  const [types, setTypes] = useState([]);
  const isLogin = mode === "login";

  const toggle = (t) => setTypes((p) => (p.includes(t) ? p.filter((x) => x !== t) : [...p, t]));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        await login(form.email, form.password);
        toast.success("Welcome back!");
      } else {
        await signup({ ...form, beds: form.beds ? Number(form.beds) : null, care_home_types: types, program: "trial" });
        toast.success("Your free trial has started!");
      }
      nav("/app");
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-cream font-body">
      <PublicNav />
      <div className="grid lg:grid-cols-2 min-h-[calc(100vh-4rem)]">
        <div className="hidden lg:flex flex-col justify-center bg-brand text-white p-14 relative overflow-hidden">
          <div className="grain absolute inset-0" />
          <div className="relative">
            <span className="label-caps text-coral">Free trial</span>
            <h2 className="text-4xl font-bold mt-4 leading-tight">Start strengthening your oral healthcare compliance today</h2>
            <p className="text-white/70 mt-4 max-w-md">Full platform access. Add your team, assign courses and watch your compliance dashboard fill up.</p>
            <ul className="mt-10 space-y-4">
              {[[Clock, "14-day free trial"], [Users, "Up to 4 staff logins during your trial"], [FileCheck2, "CQC-ready evidence export"], [ShieldCheck, "Auto-issued certificates"]].map(([Icon, txt]) => (
                <li key={txt} className="flex items-center gap-3"><Icon className="h-5 w-5 text-coral" /><span className="text-white/90">{txt}</span></li>
              ))}
            </ul>
            {!isLogin && (
              <div data-testid="signup-chat-box" className="mt-10 rounded-2xl border border-white/15 bg-white/10 backdrop-blur p-5">
                <div className="flex items-center gap-2 font-heading font-bold"><MessageCircle className="h-5 w-5 text-coral" />Have a question? Let's have a chat.</div>
                <p className="text-sm text-white/75 mt-2">Not sure where to start or how KOHC could support your care home? Book a free, informal online chat with one of our dental care professionals. Ask questions, explore the platform and see how we can help.</p>
                <a href="https://calendly.com/kohc-dental/informal-chat" target="_blank" rel="noreferrer" data-testid="signup-chat-link"
                  className="mt-4 inline-flex items-center justify-center gap-2 w-full h-11 rounded-full bg-white text-brand font-semibold hover:bg-cream transition-colors">
                  Book a Free Informal Chat <ExternalLink className="h-4 w-4" />
                </a>
                <p className="text-xs text-white/60 mt-3 text-center">No pressure. No obligation. Just friendly, practical advice.</p>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-center p-6 md:p-14">
          <div className="w-full max-w-md">
            <h1 className="text-3xl font-bold text-brand tracking-tight">{isLogin ? "Log in" : "Start your free trial"}</h1>
            <p className="text-muted-foreground mt-2">{isLogin ? "Access your KOHC dashboard." : "No card needed. Try everything free for 14 days, then choose a licence that fits your home."}</p>
            {!isLogin && <div className="mt-6"><IntroOfferBanner variant="compact" /></div>}
            <form onSubmit={submit} className="space-y-4 mt-8">
              {!isLogin && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Your name</Label><Input data-testid="auth-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                    <div><Label>Role</Label><Input data-testid="auth-role" placeholder="e.g. Registered Manager" value={form.job_role} onChange={(e) => setForm({ ...form, job_role: e.target.value })} /></div>
                  </div>
                  <div className="grid grid-cols-[1fr_140px] gap-3">
                    <div><Label>Care home name</Label><Input data-testid="auth-home" required value={form.care_home_name} onChange={(e) => setForm({ ...form, care_home_name: e.target.value })} /></div>
                    <div><Label>Registered beds</Label><Input data-testid="auth-beds" type="number" min={1} max={2000} required placeholder="e.g. 42" value={form.beds} onChange={(e) => setForm({ ...form, beds: e.target.value })} /></div>
                  </div>
                </>
              )}
              <div><Label>Work email</Label><Input data-testid="auth-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div>
                <div className="flex items-center justify-between">
                  <Label>Password</Label>
                  {isLogin && <Link data-testid="forgot-password-link" to="/forgot-password" className="text-sm text-brand font-medium hover:underline">Forgot password?</Link>}
                </div>
                <PasswordInput testId="auth-password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
              </div>
              {!isLogin && (
                <div>
                  <Label>Care home type <span className="text-muted-foreground font-normal">(select all that apply)</span></Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {CARE_HOME_TYPES.map((t) => (
                      <label key={t} className="flex items-center gap-2 text-sm cursor-pointer">
                        <Checkbox data-testid={`auth-type-${t}`} checked={types.includes(t)} onCheckedChange={() => toggle(t)} />{t}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <Button data-testid="auth-submit" type="submit" disabled={loading} className="w-full h-11 rounded-full bg-coral hover:bg-coral-hover text-white">
                {submitLabel(loading, isLogin)}
              </Button>
            </form>
            <p className="text-sm text-muted-foreground mt-6 text-center">
              {isLogin ? <>New to KOHC? <Link to="/signup" className="text-brand font-semibold hover:underline">Start a free trial</Link></>
                : <>Already have an account? <Link to="/login" className="text-brand font-semibold hover:underline">Log in</Link></>}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
